<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  categories: {
    type: Array,
    required: true,
    validator: value =>
      value.every(category => 'key' in category && 'items' in category),
  },
  draggable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select', 'dragstart']);

const { t } = useI18n();

const search = ref('');
const collapsedCategories = ref({});

const toggleCategory = key => {
  collapsedCategories.value = {
    ...collapsedCategories.value,
    [key]: !collapsedCategories.value[key],
  };
};

const isSearching = computed(() => search.value.trim().length > 0);

const visibleCategories = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.categories;

  return props.categories
    .map(category => ({
      ...category,
      items: category.items.filter(
        item =>
          item.label.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
      ),
    }))
    .filter(category => category.items.length);
});
</script>

<template>
  <div class="flex flex-col gap-3 min-h-0">
    <div class="relative flex-shrink-0">
      <span
        class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
      />
      <input
        v-model="search"
        type="search"
        :placeholder="t('FLOWS.EDITOR.PALETTE_SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 !pl-9 !pr-3 text-sm rounded-lg outline outline-1 outline-n-weak focus:outline-n-brand bg-n-solid-1 text-n-slate-12"
      />
    </div>

    <div class="flex flex-col gap-1 overflow-y-auto min-h-0">
      <div v-for="category in visibleCategories" :key="category.key">
        <button
          type="button"
          class="flex items-center justify-between w-full px-1 py-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10 hover:text-n-slate-12"
          @click="toggleCategory(category.key)"
        >
          {{ category.label }}
          <span
            class="i-lucide-chevron-down transition-transform"
            :class="
              !isSearching && collapsedCategories[category.key]
                ? '-rotate-90'
                : ''
            "
          />
        </button>

        <div
          v-show="isSearching || !collapsedCategories[category.key]"
          class="flex flex-col"
        >
          <button
            v-for="item in category.items"
            :key="item.type"
            type="button"
            :draggable="draggable"
            class="group flex items-start gap-3 w-full text-left px-2 py-2 rounded-md border-l-2 border-transparent hover:border-n-blue-9 hover:bg-n-slate-3 cursor-grab active:cursor-grabbing"
            @dragstart="emit('dragstart', $event, item.type)"
            @click="emit('select', item.type)"
          >
            <span
              class="flex-shrink-0 size-8 rounded-lg flex items-center justify-center mt-0.5"
              :class="item.colors.bg"
            >
              <span :class="[item.icon, item.colors.text]" />
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ item.label }}
              </span>
              <span
                v-if="item.description"
                class="block text-xs text-n-slate-10 line-clamp-2"
              >
                {{ item.description }}
              </span>
            </span>
          </button>
        </div>
      </div>

      <p
        v-if="!visibleCategories.length"
        class="px-2 py-4 text-sm text-center text-n-slate-10"
      >
        {{ t('FLOWS.EDITOR.PALETTE_NO_RESULTS') }}
      </p>
    </div>
  </div>
</template>
